import React from 'react';
import { Tab, ReferenceField, useShowController, useTranslate, usePermissions } from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import cx from 'classnames';

import { Show, PaperTabbedShowLayout } from '~/layout';
import { NoPermissions, TextField } from '~/components/ra';
import {
  TextField as TextFieldComponent,
  StatusField,
  H2,
  ChipField,
  BooleanField,
} from '~/components';
import IdentityAuditLog from './IdentityAuditLog';
import { Identity } from '~/types';
import IdentityActions from './components/IdentityActions';
import { isEmpty } from 'lodash';
import { Longdash } from '~/utils';
import { useEditMode } from '~/hooks/useEditModeContext';
import { useChangelogRecord } from '~/hooks/useChangelogContext';

export const l = (key: string) => `resources.team.fields.${key}`;

const IdentityShow = (props: any) => {
  const { permissions } = usePermissions();

  const showController = useShowController(props);
  const { editMode } = useEditMode();
  const showRecord: Identity | undefined = useChangelogRecord(showController.record);

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const classes = useStyles();

  if (!permissions) return null;
  if (!permissions.includes('identity.view')) return <NoPermissions />;

  const is2FaEnabled =
    showRecord?.totp ||
    !!(showRecord?.requiredActions || []).find((item) => item === 'CONFIGURE_TOTP');
  const emailVerified = !!showRecord?.emailVerified;

  return (
    <Show {...props} actions={null}>
      <PaperTabbedShowLayout>
        <Tab label={l('profile')}>
          <div className={cx(classes.section, classes.tabContent)}>
            <Grid
              container
              className={classes.bottomSpacing2}
              justify='space-between'
              alignItems='center'
            >
              <Grid item xs={8}>
                <H2 topSpacing={0} bottomSpacing={0}>
                  {t('personalData')}
                </H2>
              </Grid>
              <Grid item container xs={4} spacing={2} id='section-actions' justify='flex-end'>
                {editMode === 'VIEW' && (
                  <IdentityActions identityRecord={showRecord} permissions={permissions} />
                )}
              </Grid>
            </Grid>
            <div className={classes.container}>
              <Grid container item spacing={2} xs={10}>
                <Grid item xs={4}>
                  <TextField source='username' label={t('username')} record={showRecord} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='firstName' label={t('firstName')} record={showRecord} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='lastName' label={t('lastName')} record={showRecord} />
                </Grid>
                <Grid item xs={4}>
                  {showRecord?.deleted ? (
                    <TextFieldComponent label={t('status')}>
                      <StatusField status='DISABLED' />
                    </TextFieldComponent>
                  ) : (
                    <TextFieldComponent label={t('status')}>
                      <StatusField status='ENABLED' />
                    </TextFieldComponent>
                  )}
                </Grid>
                <Grid item xs={4}>
                  <BooleanField value={is2FaEnabled} label={t('2FA')} />
                </Grid>
                <Grid item xs={4}>
                  <BooleanField value={emailVerified} label={t('emailVerified')} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='email' label={t('email')} record={showRecord} />
                </Grid>
                <Grid item xs={4}>
                  <TextField source='phoneNumber' label={t('phoneNumber')} record={showRecord} />
                </Grid>
                <Grid item xs={4}>
                  <TextFieldComponent label={t('requiredActions')} multiline>
                    {!isEmpty(showRecord?.requiredActions)
                      ? showRecord?.requiredActions?.map((item: any) => {
                          return <ChipField label={item} />;
                        })
                      : Longdash}
                  </TextFieldComponent>
                </Grid>
                <Grid item xs={4}>
                  <ReferenceField
                    source='roleId'
                    reference='roles'
                    link={false}
                    record={showRecord}
                    basePath={showController.basePath}
                    resource={showController.resource}
                  >
                    <TextField source='name' label={t('roleId')} />
                  </ReferenceField>
                </Grid>
              </Grid>
            </div>
          </div>
        </Tab>
        <Tab label={l('actionLog')} path='actionLog' disabled={editMode !== 'VIEW'}>
          <div className={classes.tabContent}>
            <IdentityAuditLog identityId={showRecord?.id as string} />
          </div>
        </Tab>
      </PaperTabbedShowLayout>
    </Show>
  );
};

const useStyles = makeStyles((theme) => ({
  tabContent: {
    minHeight: 500,
  },
  container: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  section: {
    '& #section-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& #section-actions': {
        opacity: 1,
      },
    },
  },
  bottomSpacing2: {
    marginBottom: theme.spacing(2),
  },
}));

export default IdentityShow;
